/**
 * Money is stored and computed in integer minor units (cents), never floats.
 * See docs/technical-design.md → "Money as integers".
 *
 * ROUNDING RULE (the one documented rule): any operation that produces a
 * fractional minor unit rounds half away from zero, once, at the end of the
 * operation. Management fees are expressed in basis points (1 bp = 0.01%) so the
 * rate itself is always an integer.
 */

export const CURRENCIES = ['KES', 'USD', 'UGX', 'TZS'] as const;
export type Currency = (typeof CURRENCIES)[number];

/** Minor units per major unit. UGX has no minor unit in practice. */
const MINOR_UNITS: Record<Currency, number> = {
  KES: 100,
  USD: 100,
  UGX: 1,
  TZS: 100,
};

export interface MoneyJson {
  amountMinor: number;
  currency: Currency;
}

export class CurrencyMismatchError extends Error {
  constructor(a: Currency, b: Currency) {
    super(`Currency mismatch: ${a} vs ${b}`);
    this.name = 'CurrencyMismatchError';
  }
}

function roundHalfAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

function assertMinor(amountMinor: number): void {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError(`Money amount must be a safe integer of minor units, got ${amountMinor}`);
  }
}

export class Money {
  private constructor(
    readonly amountMinor: number,
    readonly currency: Currency,
  ) {
    assertMinor(amountMinor);
  }

  static ofMinor(amountMinor: number, currency: Currency): Money {
    return new Money(amountMinor, currency);
  }

  /** How many decimal places a currency's minor unit allows, e.g. 2 for KES, 0 for UGX. For clamping decimal input. */
  static decimalPlaces(currency: Currency): number {
    return Math.log10(MINOR_UNITS[currency]);
  }

  /** Parse a decimal string such as "1250.50". Rejects more precision than the currency allows. */
  static parse(decimal: string, currency: Currency): Money {
    const factor = MINOR_UNITS[currency];
    const places = Math.log10(factor);
    const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(decimal.trim());
    if (!match) throw new RangeError(`Invalid money string: "${decimal}"`);
    const [, sign, whole, frac = ''] = match;
    if (frac.length > places) throw new RangeError(`"${decimal}" has more than ${places} decimal places for ${currency}`);
    const minor = Number(whole) * factor + Number(frac.padEnd(places, '0') || '0');
    return new Money(sign ? -minor : minor, currency);
  }

  static zero(currency: Currency): Money {
    return new Money(0, currency);
  }

  static fromJSON(json: MoneyJson): Money {
    return new Money(json.amountMinor, json.currency);
  }

  static sum(items: readonly Money[], currency: Currency): Money {
    return items.reduce((acc, m) => acc.add(m), Money.zero(currency));
  }

  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.amountMinor + other.amountMinor, this.currency);
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.amountMinor - other.amountMinor, this.currency);
  }

  /** Multiply by an integer quantity (e.g. days of detention). */
  times(quantity: number): Money {
    if (!Number.isInteger(quantity)) throw new RangeError('Use percentOf/ratio for fractional multipliers');
    return new Money(this.amountMinor * quantity, this.currency);
  }

  /** Apply a rate in basis points, rounding once per the documented rule. 1000 bps = 10%. */
  basisPoints(bps: number): Money {
    if (!Number.isInteger(bps)) throw new RangeError('Basis points must be an integer');
    return new Money(roundHalfAwayFromZero((this.amountMinor * bps) / 10_000), this.currency);
  }

  /**
   * Split into n parts that sum exactly to this amount; the remainder goes to the
   * first parts, so no minor unit is ever created or lost.
   */
  allocate(parts: number): Money[] {
    if (!Number.isInteger(parts) || parts <= 0) throw new RangeError('parts must be a positive integer');
    const base = Math.trunc(this.amountMinor / parts);
    let remainder = this.amountMinor - base * parts;
    const step = Math.sign(remainder);
    return Array.from({ length: parts }, () => {
      const extra = remainder !== 0 ? step : 0;
      remainder -= extra;
      return new Money(base + extra, this.currency);
    });
  }

  /**
   * Split in proportion to integer weights (e.g. days) into parts that sum exactly to this amount. Each part is its
   * floor share of the absolute amount; the leftover minor units go to the parts with the largest fractional share, the
   * earlier part first on a tie. The sign is applied afterwards, so a negative amount splits like its positive twin.
   */
  allocateByWeights(weights: readonly number[]): Money[] {
    if (weights.length === 0 || weights.some((weight) => !Number.isInteger(weight) || weight < 0)) {
      throw new RangeError('weights must be non-negative integers');
    }
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total === 0) throw new RangeError('weights must not all be zero');

    const absolute = Math.abs(this.amountMinor);
    const shares = weights.map((weight, index) => ({ index, floor: Math.floor((absolute * weight) / total), fraction: (absolute * weight) % total }));
    const leftover = absolute - shares.reduce((sum, share) => sum + share.floor, 0);
    const bumped = new Set(
      [...shares]
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
        .slice(0, leftover)
        .map((share) => share.index),
    );
    const sign = this.amountMinor < 0 ? -1 : 1;
    return shares.map((share) => new Money(sign * (share.floor + (bumped.has(share.index) ? 1 : 0)), this.currency));
  }

  negate(): Money {
    return new Money(-this.amountMinor, this.currency);
  }

  isZero(): boolean {
    return this.amountMinor === 0;
  }

  isNegative(): boolean {
    return this.amountMinor < 0;
  }

  equals(other: Money): boolean {
    return this.currency === other.currency && this.amountMinor === other.amountMinor;
  }

  compare(other: Money): -1 | 0 | 1 {
    this.assertSameCurrency(other);
    return Math.sign(this.amountMinor - other.amountMinor) as -1 | 0 | 1;
  }

  /** Decimal string without grouping, e.g. "1250.50". For display use Intl.NumberFormat. */
  toDecimalString(): string {
    const factor = MINOR_UNITS[this.currency];
    const places = Math.log10(factor);
    const abs = Math.abs(this.amountMinor);
    const whole = Math.trunc(abs / factor);
    const frac = places > 0 ? '.' + String(abs % factor).padStart(places, '0') : '';
    return `${this.amountMinor < 0 ? '-' : ''}${whole}${frac}`;
  }

  toJSON(): MoneyJson {
    return { amountMinor: this.amountMinor, currency: this.currency };
  }

  private assertSameCurrency(other: Money): void {
    if (other.currency !== this.currency) throw new CurrencyMismatchError(this.currency, other.currency);
  }
}
